import React, { useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';

// Componente unico che sostituisce i tre modali Bootstrap fatti a mano.
// Prima ognuno riscriveva backdrop, fuga con Escape e blocco dello scroll a
// modo suo (o non lo faceva affatto): qui la logica di accessibilita vive in
// un solo posto.

// Contatore a modulo: se piu modali fossero aperti insieme, lo scroll del
// body deve restare bloccato finche' l'ultimo non si chiude, non al primo
// che si smonta.
let openModalCount = 0;

function lockBodyScroll() {
    openModalCount += 1;
    if (openModalCount === 1) {
        document.body.style.overflow = 'hidden';
    }
}

function unlockBodyScroll() {
    openModalCount = Math.max(0, openModalCount - 1);
    if (openModalCount === 0) {
        document.body.style.overflow = '';
    }
}

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function Modal({ open, title, onClose, footer, size = 'md', children }) {
    const dialogRef = useRef(null);
    const previousFocusRef = useRef(null);
    const titleId = useId();

    // Il body si blocca/sblocca e il fuoco si sposta solo quando `open`
    // cambia: niente hook dopo un return condizionale, quindi la guardia
    // `if (!open) return` vive dentro l'effetto, non prima degli hook.
    useEffect(() => {
        if (!open) return undefined;

        previousFocusRef.current = document.activeElement;
        lockBodyScroll();
        dialogRef.current?.focus();

        return () => {
            unlockBodyScroll();
            // Il fuoco torna a chi lo aveva prima di aprire il modale: chi
            // naviga da tastiera non deve ritrovarsi in cima alla pagina.
            if (previousFocusRef.current instanceof HTMLElement) {
                previousFocusRef.current.focus();
            }
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
                return;
            }

            if (event.key !== 'Tab') return;

            // Trappola del fuoco: Tab e Shift+Tab restano dentro il modale
            // finche' e' aperto, altrimenti scapperebbe verso la pagina
            // sottostante che e ancora presente nel DOM.
            const focusable = dialogRef.current?.querySelectorAll(FOCUSABLE_SELECTOR);
            if (!focusable || focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, onClose]);

    if (!open) return null;

    const handleBackdropClick = (event) => {
        // Chiude solo se il click parte dal velo stesso: un click che inizia
        // dentro il riquadro e finisce (per trascinamento) sopra il velo non
        // deve chiudere il modale.
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    return createPortal(
        <div className="modal-backdrop" onMouseDown={handleBackdropClick}>
            <div
                ref={dialogRef}
                className={`modal modal--${size}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
            >
                <div className="modal-header">
                    <h4 id={titleId} className="modal-title">
                        {title}
                    </h4>
                </div>
                <div className="modal-body">{children}</div>
                {footer && <div className="modal-footer">{footer}</div>}
            </div>
        </div>,
        document.body,
    );
}
